import type { Socket } from 'socket.io-client';
import { useAuthStore } from '../store/auth.store';
import { recoverDeviceBinding, recoverSocketSession } from './api';

export type SessionInvalidCode = 'SESSION_REVOKED' | 'SESSION_EXPIRED' | 'AUTH_UNAVAILABLE';
const codes = new Set(['SESSION_REVOKED', 'SESSION_EXPIRED', 'AUTH_UNAVAILABLE']);

/** Owns a single socket lifetime, including recovery continuations after route cleanup. */
export function employeeSocketSession(
  socket: Socket,
  token: string,
  pause: (paused: boolean) => void,
) {
  const revision = useAuthStore.getState().sessionRevision;
  let alive = true;
  let stopped = false;
  let recovering = false;
  const current = () =>
    alive &&
    useAuthStore.getState().sessionRevision === revision &&
    useAuthStore.getState().accessToken === token;
  const canAct = () => current() && !stopped;
  const stop = () => {
    stopped = true;
    socket.disconnect();
    pause(true);
  };
  const recover = async () => {
    if (!current() || recovering) return;
    recovering = true;
    const result = await recoverSocketSession(current);
    recovering = false;
    // A replacement token causes the owning React effect to create a fresh socket.
    if (result === 'refreshed' && current()) {
      stopped = false;
      pause(false);
      socket.connect();
    }
  };
  const invalid = (code: unknown) => {
    if (!current() || stopped) return;
    stop();
    if (code === 'SESSION_REVOKED') {
      useAuthStore.getState().clearAuth();
      window.location.href = '/login' + '?session=revoked';
    } else if (code === 'SESSION_EXPIRED' || code === undefined) {
      void recover();
    }
  };
  socket.on('session:invalid', (notice: { code?: unknown }) => {
    if (codes.has(String(notice?.code))) invalid(notice.code);
  });
  socket.on('device-binding:revoked', () => {
    if (!canAct()) return;
    stop();
    void recoverDeviceBinding('DEVICE_BINDING_REVOKED', true, current);
  });
  socket.on('connect_error', (error: Error & { data?: { code?: unknown } }) => {
    if (codes.has(String(error.data?.code))) invalid(error.data?.code);
  });
  socket.on('disconnect', (reason: string) => {
    if (reason === 'io server disconnect') invalid(undefined);
  });
  return {
    canAct,
    current,
    retry: () => {
      if (!current() || recovering || !stopped) return;
      stopped = false;
      pause(false);
      socket.connect();
    },
    dispose: () => {
      alive = false;
      stopped = true;
      socket.removeAllListeners();
      socket.offAny();
      socket.disconnect();
    },
  };
}
